import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { api } from "../App";
import { 
  ArrowLeft, 
  Plus, 
  Minus, 
  DollarSign, 
  Calendar, 
  User, 
  Home,
  Send,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  FileText,
  Shield,
  Link2,
  Copy,
  ExternalLink,
  Trash2
} from "lucide-react";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "$0.00";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const getStatusBadge = (status) => {
  const styles = {
    paid: "bg-green-500/20 text-green-400 border-green-500/30",
    overpaid: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    partial: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    pending: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    late: "bg-red-500/20 text-red-400 border-red-500/30"
  };
  return styles[status] || styles.pending;
};

const getStatusIcon = (status) => {
  switch (status) {
    case "paid":
    case "overpaid":
      return <CheckCircle className="w-4 h-4" />;
    case "partial":
      return <Clock className="w-4 h-4" />;
    case "late":
      return <AlertCircle className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

export default function UnitRentLedger() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ledger, setLedger] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  
  // Dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  
  // Form states
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: "cash",
    notes: ""
  });
  
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: "",
    description: "",
    date: new Date().toISOString().split('T')[0],
    type: "charge" // charge or credit
  });

  const [reminderForm, setReminderForm] = useState({
    reminder_type: "upcoming",
    custom_message: "",
    include_balance: true
  });

  // Lease & Deposit states
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [portalLink, setPortalLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositData, setDepositData] = useState(null);
  const [leaseDialogOpen, setLeaseDialogOpen] = useState(false);
  const [leaseDocuments, setLeaseDocuments] = useState([]);
  
  const [deductionForm, setDeductionForm] = useState({
    amount: "",
    reason: "",
    date: new Date().toISOString().split('T')[0],
    category: "damages"
  });

  const [leaseDocForm, setLeaseDocForm] = useState({
    document_name: "",
    document_url: "",
    document_type: "lease",
    signed_date: "",
    notes: ""
  });

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/units/${unitId}/rent-ledger?year=${year}`);
      setLedger(response.data);
    } catch (error) {
      toast.error("Failed to load rent ledger");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepositData = async () => {
    try {
      const response = await api.get(`/units/${unitId}/deposit-deductions`);
      setDepositData(response.data);
    } catch (error) {
      console.error("Failed to load deposit data");
    }
  };

  const fetchLeaseDocuments = async () => {
    try {
      const response = await api.get(`/units/${unitId}/lease-documents`);
      setLeaseDocuments(response.data);
    } catch (error) {
      console.error("Failed to load lease documents");
    }
  };

  useEffect(() => {
    if (unitId) {
      fetchLedger();
      fetchDepositData();
      fetchLeaseDocuments();
    }
  }, [unitId, year]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedMonth) return;
    
    // Prevent double submission
    if (loading) return;
    setLoading(true);
    
    try {
      await api.post(`/units/${unitId}/rent-payment`, {
        unit_id: unitId,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        year: year,
        month: selectedMonth,
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      });
      toast.success("Payment recorded!");
      setPaymentDialogOpen(false);
      setPaymentForm({ amount: "", payment_date: new Date().toISOString().split('T')[0], payment_method: "cash", notes: "" });
      setSelectedMonth(null);
      await fetchLedger();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdjustment = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    setLoading(true);
    
    try {
      const amount = parseFloat(adjustmentForm.amount);
      await api.post(`/units/${unitId}/balance-adjustment`, {
        unit_id: unitId,
        amount: adjustmentForm.type === "credit" ? -amount : amount,
        description: adjustmentForm.description,
        date: adjustmentForm.date
      });
      toast.success(`${adjustmentForm.type === "charge" ? "Charge" : "Credit"} recorded!`);
      setAdjustmentDialogOpen(false);
      setAdjustmentForm({ amount: "", description: "", date: new Date().toISOString().split('T')[0], type: "charge" });
      await fetchLedger();
    } catch (error) {
      console.error("Adjustment error:", error);
      toast.error(error.response?.data?.detail || "Failed to add adjustment");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePortalLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await api.post(`/units/${unitId}/generate-tenant-portal-link`);
      setPortalLink(response.data);
      toast.success("Portal link generated!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to generate link";
      toast.error(msg);
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyPortalLink = () => {
    if (portalLink) {
      const fullUrl = `${window.location.origin}${portalLink.portal_url}`;
      navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleAddDeduction = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/units/${unitId}/deposit-deductions`, {
        unit_id: unitId,
        amount: parseFloat(deductionForm.amount),
        reason: deductionForm.reason,
        date: deductionForm.date,
        category: deductionForm.category
      });
      toast.success("Deduction added!");
      setDeductionForm({ amount: "", reason: "", date: new Date().toISOString().split('T')[0], category: "damages" });
      fetchDepositData();
    } catch (error) {
      toast.error("Failed to add deduction");
    }
  };

  const handleDeleteDeduction = async (deductionId) => {
    try {
      await api.delete(`/units/${unitId}/deposit-deductions/${deductionId}`);
      toast.success("Deduction removed");
      fetchDepositData();
    } catch (error) {
      toast.error("Failed to delete deduction");
    }
  };

  const handleRefundDeposit = async () => {
    try {
      const response = await api.post(`/units/${unitId}/refund-deposit`);
      toast.success(response.data.message);
      fetchDepositData();
      fetchLedger();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to process refund";
      toast.error(msg);
    }
  };

  const handleAddLeaseDoc = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/units/${unitId}/lease-documents`, {
        unit_id: unitId,
        ...leaseDocForm
      });
      toast.success("Document added!");
      setLeaseDocForm({ document_name: "", document_url: "", document_type: "lease", signed_date: "", notes: "" });
      fetchLeaseDocuments();
    } catch (error) {
      toast.error("Failed to add document");
    }
  };

  const handleDeleteLeaseDoc = async (docId) => {
    try {
      await api.delete(`/units/${unitId}/lease-documents/${docId}`);
      toast.success("Document removed");
      fetchLeaseDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleSendReminder = async (e) => {
    e.preventDefault();
    if (!ledger?.tenant_email) {
      toast.error("No tenant email on file");
      return;
    }
    setSendingReminder(true);
    try {
      const response = await api.post(`/units/${unitId}/send-rent-reminder`, {
        reminder_type: reminderForm.reminder_type,
        custom_message: reminderForm.custom_message || null,
        include_balance: reminderForm.include_balance
      });
      toast.success(`Reminder sent to ${ledger.tenant_email}!`);
      setReminderDialogOpen(false);
      setReminderForm({ reminder_type: "upcoming", custom_message: "", include_balance: true });
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send reminder";
      toast.error(msg);
    } finally {
      setSendingReminder(false);
    }
  };

  const openPaymentDialog = (month) => {
    setSelectedMonth(month);
    if (ledger) {
      setPaymentForm({
        ...paymentForm,
        amount: ledger.rent_amount.toString()
      });
    }
    setPaymentDialogOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!ledger) {
    return (
      <Layout>
        <div className="text-center text-zinc-500 py-12">Unit not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              className="border-white/10 rounded-sm"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Unit {ledger.unit_number} - Rent Ledger</h1>
              <p className="text-zinc-500 text-sm">12-Month Payment Tracking</p>
            </div>
          </div>
          
          {/* Year Navigation */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="border-white/10 rounded-sm"
              onClick={() => setYear(year - 1)}
              data-testid="prev-year-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-white font-mono px-4">{year}</span>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-white/10 rounded-sm"
              onClick={() => setYear(year + 1)}
              data-testid="next-year-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tenant Info Card */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Tenant</p>
                <p className="text-white font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  {ledger.tenant_name || "Vacant"}
                </p>
                {ledger.tenant_email && (
                  <a href={`mailto:${ledger.tenant_email}`} className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                    <Mail className="w-3 h-3" /> {ledger.tenant_email}
                  </a>
                )}
                {ledger.tenant_phone && (
                  <a href={`tel:${ledger.tenant_phone}`} className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" /> {ledger.tenant_phone}
                  </a>
                )}
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Monthly Rent</p>
                <p className="text-white font-mono">{formatCurrency(ledger.rent_amount)}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Deposit</p>
                <p className="text-white font-mono">{formatCurrency(ledger.deposit)}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Lease Period</p>
                <p className="text-white text-sm">
                  {ledger.lease_start ? `${ledger.lease_start} - ${ledger.lease_end || 'Present'}` : 'Not set'}
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Current Balance</p>
                <p className={`font-mono font-bold ${ledger.running_balance > 0 ? 'text-red-400' : ledger.running_balance < 0 ? 'text-green-400' : 'text-white'}`}>
                  {ledger.running_balance > 0 ? `${formatCurrency(ledger.running_balance)} OWED` : 
                   ledger.running_balance < 0 ? `${formatCurrency(Math.abs(ledger.running_balance))} CREDIT` : 
                   '$0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Due ({year})</p>
              <p className="text-2xl font-mono text-white">{formatCurrency(ledger.total_due)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Paid ({year})</p>
              <p className="text-2xl font-mono text-green-400">{formatCurrency(ledger.total_paid)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Outstanding</p>
              <p className={`text-2xl font-mono ${ledger.running_balance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {formatCurrency(Math.max(0, ledger.running_balance))}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Security Deposit</p>
              <p className="text-2xl font-mono text-amber-400">{formatCurrency(ledger.deposit)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Lease & Deposit Quick Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-blue-500" /> Lease Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Start Date</span>
                <span className="text-white">{ledger.lease_start || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">End Date</span>
                <span className="text-white">{ledger.lease_end || 'Month-to-month'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Documents</span>
                <span className="text-white">{leaseDocuments.length} files</span>
              </div>
              <Dialog open={leaseDialogOpen} onOpenChange={setLeaseDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full mt-2 border-blue-500/50 text-blue-400 hover:bg-blue-500/10 rounded-sm">
                    <FileText className="w-3 h-3 mr-2" /> Manage Documents
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-white">Lease Documents</DialogTitle>
                    <DialogDescription className="text-zinc-500">Manage lease agreements and related documents</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[300px] overflow-y-auto">
                    {leaseDocuments.length > 0 ? leaseDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-white/5 rounded">
                        <div>
                          <a href={doc.document_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                            <FileText className="w-3 h-3" /> {doc.document_name}
                          </a>
                          <p className="text-xs text-zinc-500">{doc.document_type} {doc.signed_date && `• Signed ${doc.signed_date}`}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteLeaseDoc(doc.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )) : <p className="text-zinc-500 text-center py-4">No documents uploaded</p>}
                  </div>
                  <form onSubmit={handleAddLeaseDoc} className="space-y-3 pt-4 border-t border-white/10">
                    <p className="text-zinc-300 text-sm font-medium">Add New Document</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Document name" value={leaseDocForm.document_name} onChange={(e) => setLeaseDocForm({...leaseDocForm, document_name: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" required />
                      <Select value={leaseDocForm.document_type} onValueChange={(v) => setLeaseDocForm({...leaseDocForm, document_type: v})}>
                        <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lease">Lease Agreement</SelectItem>
                          <SelectItem value="addendum">Addendum</SelectItem>
                          <SelectItem value="notice">Notice</SelectItem>
                          <SelectItem value="inspection">Inspection Report</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input placeholder="Document URL" value={leaseDocForm.document_url} onChange={(e) => setLeaseDocForm({...leaseDocForm, document_url: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" required />
                    <Button type="submit" className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-sm">Add Document</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-sm">
                <Shield className="w-4 h-4 text-amber-500" /> Security Deposit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Original Amount</span>
                <span className="text-white font-mono">{formatCurrency(ledger.deposit)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Deductions</span>
                <span className="text-red-400 font-mono">-{formatCurrency(depositData?.total_deductions || 0)}</span>
              </div>
              <div className="flex justify-between border-t border-white/10 pt-2">
                <span className="text-zinc-300 font-medium">Balance</span>
                <span className="text-white font-mono font-bold">{formatCurrency(depositData?.deposit_balance || ledger.deposit)}</span>
              </div>
              <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full mt-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 rounded-sm">
                    <Shield className="w-3 h-3 mr-2" /> Manage Deposit
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-white">Deposit Management</DialogTitle>
                    <DialogDescription className="text-zinc-500">Track deductions and process refunds</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-white/5 rounded">
                        <p className="text-xs text-zinc-500">Original</p>
                        <p className="text-white font-mono">{formatCurrency(ledger.deposit)}</p>
                      </div>
                      <div className="p-2 bg-red-500/10 rounded">
                        <p className="text-xs text-zinc-500">Deductions</p>
                        <p className="text-red-400 font-mono">-{formatCurrency(depositData?.total_deductions || 0)}</p>
                      </div>
                      <div className="p-2 bg-green-500/10 rounded">
                        <p className="text-xs text-zinc-500">Balance</p>
                        <p className="text-green-400 font-mono">{formatCurrency(depositData?.deposit_balance || ledger.deposit)}</p>
                      </div>
                    </div>
                    {depositData?.deductions?.length > 0 && (
                      <div className="space-y-2 max-h-[150px] overflow-y-auto">
                        <p className="text-zinc-400 text-sm">Deductions:</p>
                        {depositData.deductions.map((d) => (
                          <div key={d.id} className="flex items-center justify-between p-2 bg-white/5 rounded text-sm">
                            <div>
                              <p className="text-white">{d.reason}</p>
                              <p className="text-xs text-zinc-500">{d.category} • {d.date}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-red-400 font-mono">-{formatCurrency(d.amount)}</span>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDeduction(d.id)} className="text-red-400 hover:text-red-300 p-1">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <form onSubmit={handleAddDeduction} className="space-y-3 pt-4 border-t border-white/10">
                      <p className="text-zinc-300 text-sm font-medium">Add Deduction</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="number" step="0.01" placeholder="Amount" value={deductionForm.amount} onChange={(e) => setDeductionForm({...deductionForm, amount: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" required />
                        <Select value={deductionForm.category} onValueChange={(v) => setDeductionForm({...deductionForm, category: v})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="damages">Damages</SelectItem>
                            <SelectItem value="cleaning">Cleaning</SelectItem>
                            <SelectItem value="unpaid_rent">Unpaid Rent</SelectItem>
                            <SelectItem value="unpaid_utilities">Unpaid Utilities</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input placeholder="Reason for deduction" value={deductionForm.reason} onChange={(e) => setDeductionForm({...deductionForm, reason: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" required />
                      <Button type="submit" className="w-full bg-red-500 text-white hover:bg-red-600 rounded-sm">Add Deduction</Button>
                    </form>
                    <Button onClick={handleRefundDeposit} className="w-full bg-green-500 text-white hover:bg-green-600 rounded-sm" disabled={!depositData || depositData.deposit_balance <= 0}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Process Refund ({formatCurrency(depositData?.deposit_balance || 0)})
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Tenant Portal Link Button */}
          <Dialog open={portalDialogOpen} onOpenChange={setPortalDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-purple-500 text-white hover:bg-purple-600 rounded-sm" data-testid="tenant-portal-btn" disabled={!ledger?.tenant_name}>
                <Link2 className="w-4 h-4 mr-2" /> Tenant Portal Link
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-purple-500" /> Tenant Portal Access
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Generate a unique link for {ledger?.tenant_name} to view their portal
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {!portalLink ? (
                  <Button onClick={handleGeneratePortalLink} disabled={generatingLink} className="w-full bg-purple-500 text-white hover:bg-purple-600 rounded-sm">
                    {generatingLink ? (
                      <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> Generating...</>
                    ) : (
                      <>Generate Portal Link</>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-sm">
                      <p className="text-xs text-zinc-400 mb-1">Portal Link (expires {new Date(portalLink.expires_at).toLocaleDateString()})</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-purple-400 text-sm bg-black/50 p-2 rounded overflow-x-auto">
                          {window.location.origin}{portalLink.portal_url}
                        </code>
                        <Button size="sm" variant="outline" onClick={copyPortalLink} className="border-purple-500/50 text-purple-400">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={copyPortalLink} className="flex-1 bg-purple-500 text-white hover:bg-purple-600 rounded-sm">
                        <Copy className="w-4 h-4 mr-2" /> Copy Link
                      </Button>
                      <a href={portalLink.portal_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="border-purple-500/50 text-purple-400 rounded-sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                    <p className="text-xs text-zinc-500">Share this link with your tenant. They can view their rent status, lease info, and deposit balance.</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 text-black hover:bg-amber-600 rounded-sm" data-testid="add-adjustment-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Charge/Credit
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader>
                <DialogTitle className="text-white">Add Balance Adjustment</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Add a charge (tenant owes) or credit (reduce balance) to this unit.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddAdjustment} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Type</Label>
                  <Select value={adjustmentForm.type} onValueChange={(v) => setAdjustmentForm({...adjustmentForm, type: v})}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="charge">Charge (Add to Balance)</SelectItem>
                      <SelectItem value="credit">Credit (Reduce Balance)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Amount</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={adjustmentForm.amount}
                    onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 rounded-sm font-mono"
                    placeholder="0.00"
                    data-testid="adjustment-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description</Label>
                  <Input 
                    value={adjustmentForm.description}
                    onChange={(e) => setAdjustmentForm({...adjustmentForm, description: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 rounded-sm"
                    placeholder="e.g., Late fee, Utility charge, Damage deposit refund"
                    data-testid="adjustment-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Date</Label>
                  <Input 
                    type="date"
                    value={adjustmentForm.date}
                    onChange={(e) => setAdjustmentForm({...adjustmentForm, date: e.target.value})}
                    required
                    className="bg-black/50 border-white/10 rounded-sm"
                    data-testid="adjustment-date"
                  />
                </div>
                <Button type="submit" className={`w-full rounded-sm ${adjustmentForm.type === 'charge' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white`}>
                  {adjustmentForm.type === 'charge' ? 'Add Charge' : 'Apply Credit'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Send Reminder Dialog */}
          <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-500 text-white hover:bg-blue-600 rounded-sm" 
                data-testid="send-reminder-btn"
                disabled={!ledger?.tenant_email}
              >
                <Send className="w-4 h-4 mr-2" /> Send Rent Reminder
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" /> Send Rent Reminder
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  Send an email reminder to {ledger?.tenant_name || 'tenant'} at {ledger?.tenant_email || 'No email'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSendReminder} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Reminder Type</Label>
                  <Select value={reminderForm.reminder_type} onValueChange={(v) => setReminderForm({...reminderForm, reminder_type: v})}>
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming Rent Due</SelectItem>
                      <SelectItem value="overdue">Overdue Notice</SelectItem>
                      <SelectItem value="custom">Custom Message</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Custom Message (Optional)</Label>
                  <Textarea 
                    value={reminderForm.custom_message}
                    onChange={(e) => setReminderForm({...reminderForm, custom_message: e.target.value})}
                    className="bg-black/50 border-white/10 rounded-sm min-h-[100px]"
                    placeholder="Add a personal note to the reminder email..."
                    data-testid="reminder-message"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="include-balance" 
                    checked={reminderForm.include_balance}
                    onCheckedChange={(checked) => setReminderForm({...reminderForm, include_balance: checked})}
                  />
                  <Label htmlFor="include-balance" className="text-zinc-300 text-sm">Include current balance in email</Label>
                </div>

                {ledger?.running_balance > 0 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                    <p className="text-red-400 text-sm">
                      ⚠️ Current balance: <strong>{formatCurrency(ledger.running_balance)}</strong> owed
                    </p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-sm"
                  disabled={sendingReminder || !ledger?.tenant_email}
                  data-testid="send-reminder-submit"
                >
                  {sendingReminder ? (
                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send Reminder Email</>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* 12-Month Ledger Table */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              {year} Rent Ledger
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-zinc-500">Month</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Rent Due</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Adjustments</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Total Due</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Paid</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-zinc-500">Balance</th>
                    <th className="text-center p-3 text-xs uppercase tracking-wider text-zinc-500">Status</th>
                    <th className="text-center p-3 text-xs uppercase tracking-wider text-zinc-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((entry) => (
                    <tr key={entry.month} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-3 text-white font-medium">{entry.month_name}</td>
                      <td className="p-3 text-right font-mono text-zinc-300">{formatCurrency(entry.rent_due)}</td>
                      <td className={`p-3 text-right font-mono ${entry.adjustments > 0 ? 'text-red-400' : entry.adjustments < 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                        {entry.adjustments !== 0 ? (entry.adjustments > 0 ? '+' : '') + formatCurrency(entry.adjustments) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono text-white">{formatCurrency(entry.total_due)}</td>
                      <td className="p-3 text-right font-mono text-green-400">{formatCurrency(entry.amount_paid)}</td>
                      <td className={`p-3 text-right font-mono font-bold ${entry.balance > 0 ? 'text-red-400' : entry.balance < 0 ? 'text-green-400' : 'text-zinc-400'}`}>
                        {entry.balance !== 0 ? formatCurrency(entry.balance) : '-'}
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={`${getStatusBadge(entry.status)} rounded-sm font-mono text-xs inline-flex items-center gap-1`}>
                          {getStatusIcon(entry.status)}
                          {entry.status.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-primary/50 text-primary hover:bg-primary/10 rounded-sm text-xs"
                          onClick={() => openPaymentDialog(entry.month)}
                          data-testid={`record-payment-${entry.month}`}
                        >
                          <DollarSign className="w-3 h-3 mr-1" /> Record
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/20 bg-white/5">
                    <td className="p-3 text-white font-bold">TOTALS</td>
                    <td className="p-3 text-right font-mono text-white">{formatCurrency(ledger.rent_amount * 12)}</td>
                    <td className="p-3"></td>
                    <td className="p-3 text-right font-mono text-white font-bold">{formatCurrency(ledger.total_due)}</td>
                    <td className="p-3 text-right font-mono text-green-400 font-bold">{formatCurrency(ledger.total_paid)}</td>
                    <td className={`p-3 text-right font-mono font-bold ${ledger.running_balance > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {formatCurrency(ledger.running_balance)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
            <DialogHeader>
              <DialogTitle className="text-white">Record Payment</DialogTitle>
              <DialogDescription className="text-zinc-500">
                {selectedMonth && `Recording payment for ${new Date(year, selectedMonth - 1).toLocaleString('default', { month: 'long' })} ${year}`}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Amount</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  required
                  className="bg-black/50 border-white/10 rounded-sm font-mono"
                  data-testid="payment-amount"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Payment Date</Label>
                <Input 
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                  required
                  className="bg-black/50 border-white/10 rounded-sm"
                  data-testid="payment-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                  <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Notes (Optional)</Label>
                <Input 
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  className="bg-black/50 border-white/10 rounded-sm"
                  placeholder="Any additional notes..."
                  data-testid="payment-notes"
                />
              </div>
              <Button type="submit" className="w-full bg-green-500 text-white hover:bg-green-600 rounded-sm">
                <CheckCircle className="w-4 h-4 mr-2" /> Record Payment
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
